import { PARTY_LABEL } from '../audit/requirements';
import { daysOverdue, WINDOW_LABELS } from '../audit/windows';
import type { CsvCell } from '../ui/csv';
import type { CallItem } from './callList';
import type { DistrictRow, StoreRow } from './dashboard';
import { earliestDue } from './dashboard';
import { isoToCompanyDay } from '../ui/dates';
import { plural } from '../ui/plural';
import { withOwnerCell, type OwnerOf } from '../reports/ownerColumn';

/**
 * CSV rows for the Home / district boards, the district roll-up and the
 * call list (B6, owner rulings 2026-10-25) — the same rows the tables show,
 * as plain values (YYYY-MM-DD dates, whole-number grades) so a spreadsheet
 * sorts them. Pure.
 */

// The board's day (formatDay → the Eastern day): clock dates are full
// UTC timestamps, so an evening submission's UTC day would be a day late (QA 2026-10-25).
const day = (iso: string | null | undefined) => isoToCompanyDay(iso);
const grade = (g: number | null | undefined, ready: boolean) => (ready && g !== null && g !== undefined ? Math.round(g) : null);
const clockText = (c: StoreRow['clocks'][number], now: Date) => {
  const who = c.party === 'auditor' || c.party === 'store' ? ` (${PARTY_LABEL[c.party]})` : '';
  const late = c.status === 'overdue' && c.nextDue ? ` ${plural(daysOverdue(c.nextDue, now), 'day')}` : '';
  return `${c.template.name}${who}: ${WINDOW_LABELS[c.status]}${late} (last ${day(c.lastCompleted) || '—'}, due ${day(c.nextDue) || '—'})`;
};

export const BOARD_CSV_HEADER = [
  'Store number', 'Store', 'District', 'Long distance', 'Timeliness', 'Quality', 'Audit status', 'Days overdue',
  'Next due', 'Audits', 'Open tasks', 'Overdue tasks', 'Untouched overdue tasks',
] as const;

export function boardCsvRows(rows: readonly StoreRow[], now: Date, gradesReady: boolean, ownerOf?: OwnerOf | null): CsvCell[][] {
  return rows.map((r) => withOwnerCell([
    r.store.storeNumber,
    r.store.name,
    r.store.district,
    r.store.longDistance,
    grade(r.grade.timeliness, gradesReady),
    grade(r.grade.quality, gradesReady),
    WINDOW_LABELS[r.status],
    r.status === 'overdue' && r.nextDue ? daysOverdue(r.nextDue, now) : null,
    day(earliestDue(r)),
    r.clocks.map((c) => clockText(c, now)).join('; '),
    r.openTasks,
    r.overdueTasks,
    r.untouchedOverdueTasks,
  ], r.store, ownerOf));
}

export const DISTRICT_CSV_HEADER = [
  'District', 'Timeliness', 'Quality', 'Stores', 'Stores overdue', 'Not yet audited', 'Open tasks', 'Overdue tasks', 'Untouched overdue tasks',
] as const;

export function districtCsvRows(rows: readonly DistrictRow[], gradesReady: boolean): CsvCell[][] {
  return rows.map((d) => [
    d.district || '(No district)',
    grade(d.timeliness, gradesReady),
    grade(d.quality, gradesReady),
    d.stores,
    d.behind,
    d.neverAudited,
    d.openTasks,
    d.overdueTasks,
    d.untouchedOverdueTasks,
  ]);
}

/** Home's worst-10 board (owner ruling 2026-10-25): its rank, the board's columns, and why it's listed (+ Owner last, for staff). */
export const CALL_LIST_CSV_HEADER = ['Rank', ...BOARD_CSV_HEADER, 'Why it’s here'] as const;

export function callListCsvRows(items: readonly CallItem[], now: Date, gradesReady: boolean, ownerOf?: OwnerOf | null): CsvCell[][] {
  const board = boardCsvRows(items.map((i) => i.row), now, gradesReady);
  return items.map((i, n) => withOwnerCell([n + 1, ...board[n]!, i.reason], i.row.store, ownerOf));
}
